<script setup>
import {ref, onMounted, computed, onUnmounted, watch, nextTick} from 'vue';
import {useMainOrdersStore} from '@/stores/mainOrders.js';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import {gregorianDate} from '@/utils/gregorianDate.js';
import StateColumn from '@/views/orders/Shared/StateColumn.vue';
import {formatPrice} from '@/utils/priceFormatter.js';
import Filters from "@/views/orders/Filters/index.vue";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import {Icon} from "@iconify/vue";
import AllButtons from "@/views/orders/Shared/AllButtons.vue";
import {showMessage} from "@/utils/showMessage.js";
import {useOrderFilter} from "@/composables/useOrderFilter.js";
import {useFilterByState} from "@/composables/useFilterByShopState.js";
import {useRouter} from "vue-router";
import TakersPopover from "@/views/orders/Shared/TakersPopover.vue";
import SortIcon from "@/components/datatable/sortIcon.vue";

const takersPopover = ref()
const router = useRouter();
let interval = null
const cleared = ref(false)
const search = computed(() => ordersStore.accountOrdersSearch);
const ordersStore = useMainOrdersStore();
const orders = computed(() => {
  return ordersStore.getOrders('account').map(order => ({
    ...order,
    revenue: order.revenue || order.revenue === 0 ? Number(order.revenue) : null,
    price: order.price || order.price === 0 ? Number(order.price) : null,
    takersCut: order.takersCut || order.takersCut === 0 ? Number(order.takersCut) : null,
  }))
})

const fetchNewOrders = async () => {
  if (cleared.value) return;

  if (orders.value.length > 0 && !ordersStore.isGettingNewOrders) {
    await ordersStore.action_getOrders('Account');
  }

  if (!cleared.value) {
    interval = setTimeout(fetchNewOrders, 2000);
  }
};

async function applyFilters() {
  ordersStore.reset_lastUpdate();
  ordersStore.reset_orders('accountOrders');
  await ordersStore.action_getOrders('Account');
}

function orderDetails({data}) {
  const route = {name: 'account-order-details', params: {id: data.uuid || data.id}}
  router.push(route)
}

function onSortChange(params) {
  const sortOrder = params.sortOrder === 1 ? 'asc' : 'desc'
  ordersStore.setAccountOrdersSort(params.sortField, sortOrder);
}

const {filtered} = useOrderFilter(
    orders,
    search,
    'account'
);

const {filteredByState: filteredOrdersByShopState} = useFilterByState(
    filtered,
    computed(() => ordersStore.shopStateFilter),
    "shopState"
);

function copyUUIDs() {
  const selectedOrders = ordersStore.selectedAccountOrders.length > 0 ? ordersStore.selectedAccountOrders : filteredOrdersByShopState.value

  if (!selectedOrders.length) {
    alert("No UUIDs to copy!");
    return;
  }
  const uuids = selectedOrders
      .map(order => order.uuid)
      .filter(Boolean)
      .join('\n');

  navigator.clipboard.writeText(uuids)
      .then(() => {
        showMessage(selectedOrders.length === 1 ? `${selectedOrders.length} UUID copied` : `${selectedOrders.length} UUIDs copied`, 'success');
      })
      .catch(err => {
        console.error("Failed to copy UUIDs:", err);
        showMessage('Failed to copy UUIDs', 'error');
      });
}

const takersColumn = (hotOrders) => {
  if (hotOrders.length > 0) {
    if (hotOrders[0].takerName) {
      const takerName = hotOrders[0].takerName
      if (hotOrders.length > 1) {
        return takerName + ' (+' + (hotOrders.length - 1) + ')'
      }
      return takerName
    } else {
      return 'Unassigned'
    }
  }
}

watch(filteredOrdersByShopState, (newVal) => {
  ordersStore.accountFilteredOrders = newVal;
});

onMounted(() => {
  ordersStore.action_getOrders('Account');
  fetchNewOrders();
})

onUnmounted(() => {
  clearTimeout(interval);
  cleared.value = true
});
</script>

<template>
  <div class="h-full">
    <div class="flex flex-col gap-4 w-full h-full">
      <div class="flex flex-row justify-between gap-4 w-full">
        <Breadcrumbs/>
        <AllButtons summaryPermission="ORDERS_ACCOUNT_ACCESS_SUMMARY" addOrderPermission="ORDERS_ACCOUNT_UPDATE"
                    addOrderRoute="account-order-add" summary="AccountSummary" :applyFilter="applyFilters"
                    v-model="ordersStore.accountOrdersSearch" @copyUUIDs="copyUUIDs"/>
      </div>
      <div v-if="ordersStore.isLoading" class="flex items-center justify-center h-60">
        <Icon icon="eos-icons:bubble-loading" class="size-12"/>
      </div>
      <DataTable v-else
                 :nullSortOrder="-1"
                 :value="filteredOrdersByShopState"
                 scrollable
                 scrollHeight="flex"
                 :virtualScrollerOptions="{ itemSize: 32 }"
                 resizableColumns
                 removableSort
                 stripedRows
                 class="text-xs w-full"
                 size="small"
                 tableStyle="width: 100%; table-layout: fixed;"
                 :sortField="ordersStore.accountOrdersSort?.field"
                 :sortOrder="ordersStore.accountOrdersSort?.type === 'asc' ? 1 : -1"
                 row-hover
                 @row-click="orderDetails"
                 @sort="onSortChange"
                 dataKey="id"
                 v-model:selection="ordersStore.selectedAccountOrders"
                 :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300'"
      >
        <Column selectionMode="multiple" style="width: 36px;" headerStyle="width: 36px !important"></Column>
        <Column field="uuid" header="UUID" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span @click.stop class="w-full block h-full py-[0.375rem]">{{ slotProps.data.uuid }}</span>
          </template>
        </Column>
        <Column field="store" header="Store" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="game" header="Game" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="g2gSellID" header="Sell ID" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span @click.stop class="w-full block h-full py-[0.375rem]">{{ slotProps.data.g2gSellID }}</span>
          </template>
        </Column>
        <Column field="title" header="Title" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 15%; min-width: 200px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="customerName" header="Customer" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{
              slotProps.data.customerName
                  ? slotProps.data.customerName
                  : slotProps.data.customer ? slotProps.data.customer : ''
            }}
          </template>
        </Column>
        <Column field="hotOrders" header="Takers" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
              <span @click.stop class="w-full block h-full py-[0.375rem] truncate"
                    @mouseover="takersPopover.displayHotOrders($event, slotProps.data)"
                    @mouseleave="takersPopover.hidePopover()">
              {{ takersColumn(slotProps.data.hotOrders) }}
              </span>
          </template>
        </Column>
        <Column field="price" header="Price" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{ slotProps.data.price || slotProps.data.price === 0 ? formatPrice(slotProps.data.price) + ' $' : '' }}
          </template>
        </Column>
        <Column field="takersCut" header="Cut" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap text-red-400 cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{
              slotProps.data.takersCut || slotProps.data.takersCut === 0 ? formatPrice(slotProps.data.takersCut) + ' $' : ''
            }}
          </template>
        </Column>
        <Column field="revenue" header="Revenue" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span
                :class="{ '!text-red-400': slotProps.data.revenue < 0, '!text-success-500': slotProps.data.revenue > 0 }">
              {{
                slotProps.data.revenue || slotProps.data.revenue === 0 ? formatPrice(slotProps.data.revenue) + ' $' : ''
              }}
            </span>
          </template>
        </Column>
        <Column field="state" header="Status" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <StateColumn :state="slotProps.data.state"/>
          </template>
        </Column>
        <Column field="shopState" header="Shop State" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="lastUpdate" header="Last Update" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
          <span class="block w-full max-w-max">
            {{ gregorianDate(slotProps.data.lastUpdate, 'DD MMM HH:mm') }}
          </span>
          </template>
        </Column>
        <Column field="creationTime" header="Creation Time" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max">
              {{ gregorianDate(slotProps.data.creationTime, 'DD MMM YYYY') }}
            </span>
          </template>
        </Column>
        <template #empty>
          <h1 class="py-2 text-center text-lg">No Data</h1>
        </template>
      </DataTable>
      <TakersPopover ref="takersPopover"/>
    </div>
    <Filters v-if="ordersStore.isShowFilterModal" @applyFilter="applyFilters"
             :filters="['stores', 'games', 'states', 'shopStates']" service="Account"/>
  </div>
</template>